/**
 * CRM React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { crmService } from '../services';

const CRM_KEY = 'crm';
const LEADS_KEY = 'leads';
const ACTIVITIES_KEY = 'activities';
const REPORTS_KEY = 'reports';

// Leads
export function useLeads(params = {}) {
  return useQuery({
    queryKey: [CRM_KEY, LEADS_KEY, params],
    queryFn: () => crmService.getLeads(params),
    staleTime: 1 * 60 * 1000,
  });
}

export function useLead(id) {
  return useQuery({
    queryKey: [CRM_KEY, LEADS_KEY, id],
    queryFn: () => crmService.getLeadById(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: crmService.createLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => crmService.updateLead(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY, variables.id] });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: crmService.deleteLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
    },
  });
}

// Lead Stage Management
export function useMoveLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, stage }) => crmService.moveLead(id, stage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
    },
  });
}

export function useMoveLeadToNext() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: crmService.moveLeadToNext,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
    },
  });
}

export function useMoveLeadToPrevious() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: crmService.moveLeadToPrevious,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
    },
  });
}

export function useMarkLeadAsLost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }) => crmService.markLeadAsLost(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
    },
  });
}

// Lead Notes
export function useAddNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, content }) => crmService.addNote(id, content),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY, variables.id, ACTIVITIES_KEY] });
    },
  });
}

// Lead Activities
export function useLeadActivities(id) {
  return useQuery({
    queryKey: [CRM_KEY, LEADS_KEY, id, ACTIVITIES_KEY],
    queryFn: () => crmService.getActivities(id),
    enabled: !!id,
  });
}

// Lead Assignment
export function useAssignLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, assigneeId }) => crmService.assignLead(id, assigneeId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY, variables.id] });
    },
  });
}

// Lead Tour Scheduling
export function useScheduleTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...tourData }) => crmService.scheduleTour(id, tourData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY, variables.id] });
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY, variables.id, ACTIVITIES_KEY] });
    },
  });
}

// Reports
export function useFunnelReport(params = {}) {
  return useQuery({
    queryKey: [CRM_KEY, REPORTS_KEY, 'funnel', params],
    queryFn: () => crmService.getFunnelReport(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSourceAnalytics(params = {}) {
  return useQuery({
    queryKey: [CRM_KEY, REPORTS_KEY, 'sources', params],
    queryFn: () => crmService.getSourceAnalytics(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function useConversionRates(params = {}) {
  return useQuery({
    queryKey: [CRM_KEY, REPORTS_KEY, 'conversion-rates', params],
    queryFn: () => crmService.getConversionRates(params),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePerformanceReport(params = {}) {
  return useQuery({
    queryKey: [CRM_KEY, REPORTS_KEY, 'performance', params],
    queryFn: () => crmService.getPerformanceReport(params),
    staleTime: 5 * 60 * 1000,
  });
}

// Bulk Operations
export function useBulkImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: crmService.bulkImport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
    },
  });
}

export function useBulkUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadIds, updates }) => crmService.bulkUpdate(leadIds, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CRM_KEY, LEADS_KEY] });
    },
  });
}
